import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  Era5DayNormalizedWeatherService,
  Era5MonthWeatherReportService
} from '@6grain/masofa-monolith-api-client/weather-report';
import { FilterOperator } from '@app/utils/filter-operator';
import { SortType } from '@6grain/masofa-monolith-api-client/common/model/sortType';
import { WeatherIndicatorManager } from './weather-indicator.manager';

/**
 * Parameters for ERA5 data loading
 */
export interface Era5LoadParams {
  // weatherStationId?: string;
  year: string;
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * Parameters for multi-year ERA5 data loading
 */
export interface Era5MultiYearLoadParams {
  // weatherStationId: string;
  years: number[];
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * Result of ERA5 data loading
 */
export interface Era5DataResult {
  monthlyData: any[];
  normalizedData?: any[] | null;
}

/**
 * Result of multi-year ERA5 data loading
 */
export interface Era5MultiYearResult {
  yearDataArrays: any[][];
  normalizedData?: any[] | null;
}

/**
 * Manager for ERA5 weather data operations
 * Handles monthly data and normalized data loading
 */
export class Era5DataManager {
  constructor(
    private era5MonthWeatherService: Era5MonthWeatherReportService,
    private era5NormalizedService: Era5DayNormalizedWeatherService,
    private indicatorManager: WeatherIndicatorManager
  ) {}

  /**
   * Load ERA5 monthly data for a single year
   */
  loadMonthlyData(params: Era5LoadParams): Observable<any[]> {
    const filters = [
      {
        filterField: 'Year',
        filterOperator: FilterOperator.Equals,
        filterValue: params.year
      }
    ];

    return this.era5MonthWeatherService.weatherReportsEra5MonthWeatherReportGetByCoordinatesAndDateInRadiusPost(
      params.latitude ?? 0,
      params.longitude ?? 0,
      {filters}
    ).pipe(
      catchError(error => {
        console.error('Error loading ERA5 monthly data:', error);
        return of([]);
      })
    );
  }

  /**
   * Load normalized weather data by coordinates
   */
  loadNormalizedData(latitude: number, longitude: number): Observable<any[] | null> {
    return this.era5NormalizedService.weatherReportsEra5DayNormalizedWeatherGetListByCoordinatesPost({
      latitude,
      longitude
    }).pipe(
      catchError(error => {
        console.error('Error loading normalized weather data:', error);
        return of(null);
      })
    );
  }

  /**
   * Load ERA5 data with normalized data
   */
  loadDataWithNormalized(params: Era5LoadParams): Observable<Era5DataResult> {
    const monthlyData$ = this.loadMonthlyData(params);

    if (params.latitude && params.longitude) {
      const normalizedData$ = this.loadNormalizedData(params.latitude, params.longitude);

      return forkJoin({
        monthlyData: monthlyData$,
        normalizedData: normalizedData$
      });
    }

    return monthlyData$.pipe(
      map(monthlyData => ({ monthlyData, normalizedData: null }))
    );
  }

  /**
   * Load ERA5 data for multiple years
   */
  loadMultiYearData(params: Era5MultiYearLoadParams): Observable<Era5MultiYearResult> {
    const yearRequests = params.years.map(year =>
      this.loadMonthlyData({
        latitude: params.latitude,
        longitude: params.longitude,
        year: year.toString()
      })
    );

    if (params.latitude && params.longitude) {
      const normRequest = this.loadNormalizedData(params.latitude, params.longitude);

      return forkJoin([...yearRequests, normRequest]).pipe(
        map(responses => {
          const normalizedData = responses.pop() as any[] | null;
          const yearDataArrays = responses as any[][];
          return { yearDataArrays, normalizedData };
        })
      );
    }

    return forkJoin(yearRequests).pipe(
      map(yearDataArrays => ({ yearDataArrays, normalizedData: null }))
    );
  }

  /**
   * Process monthly data into a map by month
   */
  processMonthlyDataByMonth(data: any[]): Map<number, any> {
    const dataByMonth = new Map<number, any>();
    data.forEach((item: any) => {
      if (item.month) {
        dataByMonth.set(item.month, item);
      }
    });
    return dataByMonth;
  }

  /**
   * Aggregate normalized data by month
   */
  aggregateNormalizedDataByMonth(
    normalizedData: any[],
    indicator: string
  ): Map<number, any> {
    const normDataByMonth = new Map<number, any>();
    const monthlyNormData = new Map<number, { sum: number; count: number; data: any[] }>();

    normalizedData.forEach((item: any) => {
      if (item.month) {
        if (!monthlyNormData.has(item.month)) {
          monthlyNormData.set(item.month, { sum: 0, count: 0, data: [] });
        }
        const monthData = monthlyNormData.get(item.month)!;
        monthData.data.push(item);
        monthData.sum += this.indicatorManager.extractValue(item, indicator, 'era5');
        monthData.count++;
      }
    });

    // Calculate average for each month
    monthlyNormData.forEach((monthData, month) => {
      const avg = monthData.count > 0 ? monthData.sum / monthData.count : 0;
      normDataByMonth.set(month, { average: avg, ...monthData.data[0] });
    });

    return normDataByMonth;
  }

  /**
   * Get maximum month based on selected year
   */
  getMaxMonth(selectedYear: number): number {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    return selectedYear === currentYear ? currentMonth : 12;
  }

  /**
   * Extract values for months from data map
   */
  extractMonthlyValues(
    dataByMonth: Map<number, any>,
    maxMonth: number,
    indicator: string
  ): (number | null)[] {
    const values: (number | null)[] = [];

    for (let month = 1; month <= maxMonth; month++) {
      const monthData = dataByMonth.get(month);
      if (monthData) {
        values.push(this.indicatorManager.extractValue(monthData, indicator, 'era5'));
      } else {
        values.push(null);
      }
    }

    return values;
  }

  /**
   * Extract normalized values for months
   */
  extractNormalizedValues(
    normDataByMonth: Map<number, any>,
    maxMonth: number,
    indicator: string
  ): (number | null)[] {
    const normValues: (number | null)[] = [];

    for (let month = 1; month <= maxMonth; month++) {
      const normMonth = normDataByMonth.get(month);
      if (normMonth) {
        const normValue = normMonth.average !== undefined
          ? normMonth.average
          : this.indicatorManager.extractValue(normMonth, indicator, 'era5');
        normValues.push(normValue);
      } else {
        normValues.push(null);
      }
    }

    return normValues;
  }
}
